/**
 * SurfaceSwitcher — the cross-surface navigation used in the header of every
 * SystemDox surface: Capture (app.systemdox.com and the tenant web capture
 * {slug}.capture.systemdox.com), Build — the tenant console
 * ({slug}.build.systemdox.com) — and Knowledge Base, the published docs portal
 * ({slug}.kb.systemdox.com). It exposes a single Knowledge Base link back to
 * the published docs portal; Capture and Build are no longer offered here.
 *
 * This is the React renderer. Order, labels, and icon artwork come from
 * `@systemdox/surface-header`, the one shared source every surface draws from,
 * so the content cannot drift.
 *
 * Self-sufficient: it prefers the token tenant slug (TenantProvider) but falls
 * back to the slug parsed from the hostname, so it also works on full-screen
 * routes rendered outside the tenant-aware AppShell (LaunchPage, BookViewer).
 */

import { useEffect } from 'react';
import { SURFACES, type SurfaceKey } from '@systemdox/surface-header';
import { useTenant } from '@/contexts/TenantContext';
import {
  currentSurface,
  surfaceLinks,
  getTenantSlug,
  rememberTenantSlug,
  rememberedTenantSlug,
} from '@/utils/subdomain';
import { cn } from '@/lib/utils';

// Icon artwork comes from the shared surface definitions (the same strings
// the static docs header inlines), so the two renderers cannot drift.
const ICONS: Record<SurfaceKey, string> = Object.fromEntries(
  SURFACES.map((s) => [s.key, s.icon]),
) as Record<SurfaceKey, string>;

const ITEM_BASE =
  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors';

/**
 * Icon + label. Below the `sm` breakpoint the label is visually hidden (icon
 * only) but stays in the accessibility tree via `sr-only`, so every item keeps
 * an accessible name on the mobile-first surfaces.
 */
function Contents({ icon, label }: { icon: string; label: string }) {
  return (
    <>
      <svg
        className="h-4 w-4 shrink-0"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        // Static markup from the shared surface definitions, never user input.
        dangerouslySetInnerHTML={{ __html: icon }}
      />
      <span className="sr-only sm:not-sr-only">{label}</span>
    </>
  );
}

export function SurfaceSwitcher({ className }: { className?: string }) {
  const { tenant } = useTenant();
  const active = currentSurface();
  // Slug resolution, in order of authority: the auth-token tenant, then the
  // hostname (console/{slug} hosts), then a cross-subdomain cookie — the last
  // lets the capture app (which has neither) still link back to Console/Docs.
  const knownSlug = tenant?.slug ?? getTenantSlug();
  const slug = knownSlug ?? rememberedTenantSlug();

  // Persist whenever we know the slug first-hand, so other surfaces inherit it.
  useEffect(() => {
    if (knownSlug) rememberTenantSlug(knownSlug);
  }, [knownSlug]);

  // The switcher exposes Document only — Capture and Build are no longer
  // offered as cross-surface jumps. `surfaceLinks` still computes the docs URL
  // per tenant (null, rendered disabled, until the slug is known).
  const links = surfaceLinks(slug).filter(({ key }) => key === 'docs');

  return (
    <nav
      aria-label="SystemDox surfaces"
      className={cn(
        'flex items-center gap-0.5 rounded-lg border border-slate-200 bg-slate-100/70 p-0.5 dark:border-slate-700 dark:bg-slate-800/60',
        className,
      )}
    >
      {links.map(({ key, label, url }) => {
        const icon = ICONS[key];
        const isActive = key === active;

        if (isActive) {
          return (
            <span
              key={key}
              aria-current="page"
              title={label}
              className={cn(
                ITEM_BASE,
                'bg-white text-slate-900 shadow-sm dark:bg-slate-950 dark:text-slate-100',
              )}
            >
              <Contents icon={icon} label={label} />
            </span>
          );
        }

        if (!url) {
          // A real disabled control (not aria-disabled on a generic span) so
          // assistive tech announces the unavailable state and its reason.
          return (
            <button
              key={key}
              type="button"
              disabled
              aria-label={`${label} — unavailable until this workspace has a URL slug`}
              className={cn(ITEM_BASE, 'cursor-not-allowed text-slate-300 dark:text-slate-600')}
            >
              <Contents icon={icon} label={label} />
            </button>
          );
        }

        return (
          <a
            key={key}
            href={url}
            title={label}
            className={cn(
              ITEM_BASE,
              'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100',
            )}
          >
            <Contents icon={icon} label={label} />
          </a>
        );
      })}
    </nav>
  );
}
